import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import * as _ from 'lodash';
import { ProcessPreview, ProcessStatusEnum, ProcessTypeEnum } from 'src/app/shared/interfaces/process.interface';
import { AuthService } from 'src/app/shared/services/auth.service';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { AttributeItem } from '../../users/_interfaces/users-admin';
import { SubordinateGoalPreview } from '../../goals/_interfaces/subordinate-goals-preview.interface';
import { GoalsService } from '../../goals/goals.service';
import { LoadingController } from '@ionic/angular';

@Component({
    selector: 'app-assessment-process',
    templateUrl: './assessment-process.component.html'
})
export class AssessmentProcessComponent implements OnInit, OnChanges {

    @Input() processes: Array<ProcessPreview>;
    @Input() userAttributes: Array<AttributeItem>;
    @Input() subordinates: Array<SubordinateGoalPreview>;
    @Input() cycleId: number;
    @Output() triggerProcessAction: EventEmitter<{
        cycleProcessConfigId: number,
        processType: ProcessTypeEnum,
        processId: number
    }> = new EventEmitter();

    public isImpersonated = false;
    public isConsentAttributesValid = false;
    public ProcessStatusEnum = ProcessStatusEnum;
    public isAllSubordinatesConsentAttributesValid = false;
    public consentAttributes: Array<any>;
    private _loading: HTMLIonLoadingElement = null;

    constructor(
            private _translationService: TranslationService,
            private _authService: AuthService,
            private _goalsService: GoalsService,
            private _loadingController: LoadingController,
    ) {
    }

    async ngOnInit() {
        this._loading = await this._loadingController.create();
        this.isImpersonated = this._authService.isImpersonated();
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes.processes) {
            const newValue = changes.processes.currentValue;
            const oldValue = changes.processes.previousValue;
            const isEqualValues = _.isEqual(newValue, oldValue);
            if (this.cycleId && !isEqualValues) {
                if (newValue.length > 0 || changes.processes.firstChange)
                    this._isConsentAttributesValid();
                await this._isAllSubordinatesConsentAttributesValid(this.cycleId);
            }
        }
    }


    private async _isAllSubordinatesConsentAttributesValid(cycleId: number) {
        await this._loading.present();
        this._goalsService.getSubordinatesForValidation(
                cycleId
        ).subscribe(async (response) => {
            this.subordinates = response.data;
            await this._loadConsentAttributes(cycleId);
            await this._loading.dismiss();
        }, async (error) => {
            await this._loading.dismiss();
        });
    }

    private async _loadConsentAttributes(cycleId: number): Promise<void> {
        await this._loading.present();

        this._goalsService.getConsentAttributes(
                cycleId
        ).subscribe((response) => {
            this.consentAttributes = response.data;
            if (this.consentAttributes.length > 0) {
                const isAttributeOnConsent = this.consentAttributes.map((attribute) => {
                    return attribute.id;
                });
                this.subordinates.forEach((subordinate) => {
                    const userAttributes = subordinate.userAttributes;

                    const isAllAttributesOnUser = isAttributeOnConsent.every(attribute => {
                        return userAttributes.some(userAttributes => userAttributes.id === attribute);
                    });
                    if (isAllAttributesOnUser) subordinate.hasConsentAttributes = true;
                })
                this.isAllSubordinatesConsentAttributesValid = this.subordinates
                        && this.subordinates.length > 0
                        && this.subordinates.every(subordinate => {
                            return subordinate.hasConsentAttributes == true;
                        });

                if (this.isAllSubordinatesConsentAttributesValid) {
                    this.processes = this.processes.reduce((filteredProcesses, process) => {
                        if (process.processType !== ProcessTypeEnum.GoalInputValidation) {
                            filteredProcesses.push(process);
                        }
                        return filteredProcesses;
                    }, []);
                }
            }
            this._loading.dismiss();

        }, (error) => {
            this._loading.dismiss();
        });
    }

    private _isConsentAttributesValid() {
        let goalProcess = this.processes.find(p => p.processType === ProcessTypeEnum.GoalInputs);

        const userAttributes = this.userAttributes;
        const emptyAttribute: AttributeItem[] = [];
        const attributeOnConsent = goalProcess && goalProcess.consentAttributes
                ? goalProcess.consentAttributes.reduce((filteredAttributes, attribute) => {
                    if (attribute.id) {
                        filteredAttributes.push(attribute);
                    }
                    return filteredAttributes;
                }, [])
                :emptyAttribute;


        const isAllAttributesOnUser = attributeOnConsent.every(attribute => {
            return userAttributes.some(userAttribute => userAttribute.id === attribute.id);
        });

        this.isConsentAttributesValid = userAttributes
                && attributeOnConsent.length > 0
                && isAllAttributesOnUser;
    }

    public getProcessTitle(processConfig: ProcessPreview): string {
        const currentLanguage = this._translationService.getSelectedLanguage();
        if (processConfig.processType === ProcessTypeEnum.GoalInputs && this.isConsentAttributesValid)
            return processConfig.consentGoalsGlobalizationConfig[currentLanguage].title;
        return processConfig.title[currentLanguage].title;
    }

    public getStatusColor(status: ProcessStatusEnum): string {
        switch (status) {
            case ProcessStatusEnum.Declined:
                return 'danger';
            case ProcessStatusEnum.AllowedViewing:
                return 'success';
            case ProcessStatusEnum.AllowedViewingWithWarnings:
                return 'warning';
            case ProcessStatusEnum.NotEnabled:
            case ProcessStatusEnum.WaitingInput:
            default:
                return 'primary';
        }
    }

    public getButtonText(status: ProcessStatusEnum): string {
        switch (status) {
            case ProcessStatusEnum.Declined:
                return this._translationService.getTranslation('dashboard.Redo');
            case ProcessStatusEnum.AllowedViewing:
            case ProcessStatusEnum.AllowedViewingWithWarnings:
                return this._translationService.getTranslation('dashboard.See');
            case ProcessStatusEnum.WaitingInput:
                return this._translationService.getTranslation('dashboard.DoTask');
            case ProcessStatusEnum.NotEnabled:
            default:
                return '';
        }
    }

    public triggerAction(cycleProcessConfigId: number, processType: ProcessTypeEnum, processId: number) {
        this.triggerProcessAction.emit({ cycleProcessConfigId, processType, processId });
    }

}
